/**
 * @name PedidoContext
 * @author Keven Adan Bezerra | kevenadanb
 * @description Context para gerenciar o processo de pedidos no totem
 * @package src/contexts/PedidoContext/PedidoContext.tsx
 */

import React, { createContext, useCallback, useMemo, useState } from "react";
import { useDualReceiptPrinter } from "../../hooks/useDualReceiptPrinter";
import { useMultiplePrinters } from "../../hooks/useMultiplePrinters";
import { useReceiptPrinter } from "../../hooks/useReceiptPrinter";
import { useTotemServices } from "../../services/supabase/entities/useTotemServices";
import { useAlert } from "../AlertContext";
import { useCarrinho } from "../CarrinhoContext";
import { useLoading } from "../LoadingContext";
import { useOperadorContext } from "../OperadorContext";
import { useUserContext } from "../UserContext";

// Types
export interface IPedidoItem {
  produto_id: number;
  nome_produto: string;
  quantidade: number;
  preco_unitario: number;
  subtotal: number;
}

export interface IPedido {
  id?: number;
  numero_comanda: string;
  aluno_id: number;
  aluno_nome: string;
  itens: IPedidoItem[];
  valor_total: number;
  status: "pendente" | "preparando" | "pronto" | "entregue";
  data_pedido: Date;
  organizacao_id: number;
}

export interface IPedidoContext {
  // Estado do pedido
  pedidoAtual: IPedido | null;
  numeroComanda: string | null;

  // Ações
  confirmarPedido: () => Promise<boolean>;
  limparPedido: () => void;
  gerarNumeroComanda: () => string;

  // Status
  isProcessandoPedido: boolean;
}

// Context
export const PedidoContext = createContext<IPedidoContext>(
  {} as IPedidoContext,
);

// Provider Props
interface PedidoProviderProps {
  children: React.ReactNode;
}

/**
 * Provider do contexto de pedidos
 */
export const PedidoProvider: React.FC<PedidoProviderProps> = ({ children }) => {
  const { showSuccess, showError, showWarning } = useAlert();
  const { itens, total, limparCarrinho } = useCarrinho();
  const { alunoAtual, atualizarSaldoAluno } = useUserContext();
  const { withGlobalLoading } = useLoading();
  const { vendaFunctions, context } = useTotemServices();
  const { imprimirDual } = useDualReceiptPrinter();
  const { getCurrentPrintMode } = useMultiplePrinters();
  const { imprimirRecibo: imprimirReciboUnico } = useReceiptPrinter();
  const { operadorAtual } = useOperadorContext();

  const [pedidoAtual, setPedidoAtual] = useState<IPedido | null>(null);
  const [numeroComanda, setNumeroComanda] = useState<string | null>(null);
  const [isProcessandoPedido, setIsProcessandoPedido] = useState(false);
  const [ultimaImpressaoId, setUltimaImpressaoId] = useState<string | null>(
    null,
  );

  // Gerar número de comanda único
  const gerarNumeroComanda = useCallback((): string => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `${timestamp.toString().slice(-6)}${random.toString().padStart(3, "0")}`;
  }, []);

  // Confirmar pedido - processo principal
  const confirmarPedido = useCallback(async (): Promise<boolean> => {
    // 🛡️ Prevenir múltiplas execuções simultâneas
    if (isProcessandoPedido) {
      console.warn(
        "🚫 Pedido já está sendo processado, ignorando nova tentativa",
      );
      return false;
    }

    if (!alunoAtual) {
      showError("Nenhum aluno identificado");
      return false;
    }

    if (itens.length === 0) {
      showError("Carrinho vazio");
      return false;
    }

    if (!alunoAtual || alunoAtual.saldo < total) {
      showError("Saldo insuficiente");
      return false;
    }

    return withGlobalLoading(async () => {
      try {
        setIsProcessandoPedido(true);

        // 1. Gerar número da comanda
        const comanda = gerarNumeroComanda();
        setNumeroComanda(comanda);

        // 2. Preparar dados do pedido
        const itensPedido: IPedidoItem[] = itens.map((item) => ({
          produto_id: item.id,
          nome_produto: item.nome,
          quantidade: item.quantidade,
          preco_unitario: item.preco,
          subtotal: item.preco * item.quantidade,
        }));

        const pedido: IPedido = {
          numero_comanda: comanda,
          aluno_id: alunoAtual.id,
          aluno_nome: alunoAtual.nome,
          itens: itensPedido,
          valor_total: total,
          status: "pendente",
          data_pedido: new Date(),
          organizacao_id: alunoAtual.organizacao_id,
        };

        // 3. Processar venda através da API real
        if (!alunoAtual?.id || !context.organizationId) {
          throw new Error("Dados do aluno ou organização não encontrados");
        }

        const itensVenda = itens.map((item) => ({
          id_produto: item.id,
          quantidade: item.quantidade,
          valor_unitario: item.preco,
        }));

        // Chamar função do Supabase para processar a venda com validação de restrições
        const resultadoVenda = await vendaFunctions.processarComRestricoes(
          alunoAtual.id,
          itensVenda,
          0, // sem desconto
          `Pedido totem - Comanda: ${comanda}`,
        );

        if (!resultadoVenda.success) {
          const mensagemErro =
            resultadoVenda.message || "Erro ao processar venda";
          const tipoUsuario = alunoAtual?.tipo_pessoa?.toLowerCase() || "";
          const isFuncionario =
            tipoUsuario.includes("funcionário") ||
            tipoUsuario.includes("funcionario");

          // Para funcionários, tratar especificamente o erro de responsável
          if (
            isFuncionario &&
            mensagemErro.toLowerCase().includes("sem responsável")
          ) {
            throw new Error(
              "Funcionário não encontrado. Verifique os dados do funcionário.",
            );
          }

          throw new Error(mensagemErro);
        }

        // 4. Atualizar saldo do aluno
        const novoSaldo = alunoAtual.saldo - total;
        atualizarSaldoAluno(novoSaldo);

        // 5. Salvar pedido atual
        setPedidoAtual(pedido);

        // 6. Limpar carrinho
        limparCarrinho();

        // 7. Impressão automática do recibo e comanda (COM CONTROLE ANTI-DUPLICAÇÃO)
        try {
          console.log("🖨️ Iniciando impressão automática...");

          // 🛡️ Controle anti-duplicação por ID único
          const impressaoId = `${comanda}_${Date.now()}`;

          // 🛡️ Verificação dupla: estado local + controle global
          if (ultimaImpressaoId === impressaoId) {
            console.warn(
              "🚫 Impressão duplicada detectada (estado local), pulando...",
            );
            return true;
          }

          // 🛡️ Controle global baseado no número da comanda
          const chaveControleGlobal = `pedido_impressao_${comanda}`;
          if ((window as any)[chaveControleGlobal]) {
            console.warn(
              "🚫 Impressão duplicada detectada (controle global), pulando...",
            );
            return true;
          }

          // Marcar como processando
          setUltimaImpressaoId(impressaoId);
          (window as any)[chaveControleGlobal] = true;

          // Limpar controle global após um tempo
          setTimeout(() => {
            delete (window as any)[chaveControleGlobal];
            console.log(
              "🧹 Limpeza do controle de impressão do pedido:",
              comanda,
            );
          }, 5000);

          // Verificar se o hook de impressão está disponível
          console.log("📊 Estado da impressão:", {
            imprimirDualDisponivel: !!imprimirDual,
            imprimirUnicoDisponivel: !!imprimirReciboUnico,
          });

          // Preparar dados para impressão
          const dadosImpressao = {
            numero_pedido: comanda,
            data_hora: new Date().toLocaleString("pt-BR"),
            aluno_nome: alunoAtual.nome,
            aluno_matricula: alunoAtual.matricula || "N/A",
            operador_nome: operadorAtual?.nome || "Sistema",
            organizacao_nome: "IS Cantina", // TODO: Pegar do contexto de organização
            valor_total: total,
            itens: itensPedido.map((item) => ({
              nome: item.nome_produto,
              quantidade: item.quantidade,
              valor_unitario: item.preco_unitario,
              valor_total: item.subtotal,
            })),
          };

          console.log("📄 Dados para impressão:", dadosImpressao);

          // ✅ LÓGICA MELHORADA: Verificar modo de impressão com fallback inteligente
          const currentMode = getCurrentPrintMode();
          console.log(
            "🖨️ Modo de impressão detectado:",
            currentMode === "single" ? "Impressora Única" : "Duas Impressoras",
          );

          // 🎯 Executar impressão baseada no modo detectado
          let sucessoImpressao = false;

          if (currentMode === "single" && imprimirReciboUnico) {
            // 📄 Modo impressora única
            console.log("🖨️ Executando impressão em modo ÚNICO...");
            sucessoImpressao = await imprimirReciboUnico(dadosImpressao);

            if (sucessoImpressao) {
              console.log("✅ Impressão única bem-sucedida");
              setTimeout(() => {
                showSuccess("📄 Recibo impresso com sucesso!");
              }, 2000);
            } else {
              console.warn("⚠️ Falha na impressão única");
            }
          } else if (currentMode === "dual" && imprimirDual) {
            // 📄📄 Modo dual (duas impressoras)
            console.log("🖨️ Executando impressão em modo DUAL...");
            const resultadoImpressao = await imprimirDual(dadosImpressao);

            console.log("📊 Resultado da impressão dual:", resultadoImpressao);

            // Considerar sucesso se pelo menos uma impressão funcionou
            sucessoImpressao =
              resultadoImpressao.recibo || resultadoImpressao.comanda;

            if (sucessoImpressao) {
              setTimeout(() => {
                if (resultadoImpressao.recibo && resultadoImpressao.comanda) {
                  showSuccess("📄 Recibo e comanda impressos com sucesso!");
                } else if (resultadoImpressao.recibo) {
                  showSuccess(
                    "📄 Recibo impresso. Verifique impressora da cozinha.",
                  );
                } else if (resultadoImpressao.comanda) {
                  showSuccess(
                    "📄 Comanda impressa. Verifique impressora do totem.",
                  );
                }
              }, 2000);
            }
          } else {
            // 🚨 Fallback: tentar impressão única como última opção
            console.warn(
              "⚠️ Modo de impressão não determinado ou hooks indisponíveis, tentando fallback...",
            );

            if (imprimirReciboUnico) {
              console.log("🔄 Tentando fallback para impressão única...");
              sucessoImpressao = await imprimirReciboUnico(dadosImpressao);

              if (sucessoImpressao) {
                console.log("✅ Fallback de impressão única bem-sucedido");
                setTimeout(
                  () => showSuccess("📄 Recibo impresso (modo fallback)!"),
                  2000,
                );
              }
            }
          }

          // Log final do resultado
          if (!sucessoImpressao) {
            console.warn("⚠️ Todas as tentativas de impressão falharam");
            showWarning(
              "Falha na impressão. Verifique se o QZ Tray está rodando e as impressoras configuradas.",
            );
          }
        } catch (errorImpressao) {
          // Não bloquear o fluxo se a impressão falhar
          console.warn("⚠️ Erro na impressão (não crítico):", errorImpressao);
          showWarning("Impressão indisponível. Pedido processado com sucesso.");
        }

        // 8. Feedback de sucesso
        showSuccess(`Pedido confirmado! Comanda: ${comanda}`);

        return true;
      } catch (error) {
        const mensagemErro =
          error instanceof Error ? error.message : "Erro desconhecido";

        // Tratamento de erros específicos
        const erroLower = mensagemErro.toLowerCase();
        const tipoUsuario = alunoAtual?.tipo_pessoa?.toLowerCase() || "";
        const isFuncionario =
          tipoUsuario.includes("funcionário") ||
          tipoUsuario.includes("funcionario");

        if (erroLower.includes("estoque insuficiente")) {
          showError(mensagemErro);
        } else if (
          (erroLower.includes(
            "responsável financeiro não possui e-mail cadastrado",
          ) ||
            erroLower.includes(
              "responsavel financeiro não possui e-mail cadastrado",
            )) &&
          !isFuncionario
        ) {
          // Funcionários não precisam de responsável financeiro, então não exibem esse erro
          showError(
            "Não é possível finalizar a compra: o responsável financeiro do aluno não possui e-mail cadastrado. Por favor, complete os dados do responsável para prosseguir.",
          );
        } else if (
          erroLower.includes("aluno não encontrado") ||
          erroLower.includes("sem responsável")
        ) {
          // Para funcionários, adaptar a mensagem
          if (isFuncionario) {
            showError(
              "Funcionário não encontrado. Verifique os dados do funcionário.",
            );
          } else {
            showError(
              "Aluno não encontrado ou sem responsável cadastrado. Verifique os dados do aluno ou cadastre um responsável.",
            );
          }
        } else {
          showError("Erro ao processar pedido. Tente novamente.");
        }

        console.error("Erro ao confirmar pedido:", error);
        return false;
      } finally {
        setIsProcessandoPedido(false);
      }
    });
  }, [
    alunoAtual,
    itens,
    total,
    gerarNumeroComanda,
    atualizarSaldoAluno,
    limparCarrinho,
    showSuccess,
    showError,
    withGlobalLoading,
  ]);

  // Limpar pedido
  const limparPedido = useCallback(() => {
    setPedidoAtual(null);
    setNumeroComanda(null);
    setIsProcessandoPedido(false);
    setUltimaImpressaoId(null); // 🧹 Limpar controle de impressão
  }, []);

  // Context value
  const contextValue: IPedidoContext = useMemo(
    () => ({
      pedidoAtual,
      numeroComanda,
      confirmarPedido,
      limparPedido,
      gerarNumeroComanda,
      isProcessandoPedido,
    }),
    [
      pedidoAtual,
      numeroComanda,
      confirmarPedido,
      limparPedido,
      gerarNumeroComanda,
      isProcessandoPedido,
    ],
  );

  return (
    <PedidoContext.Provider value={contextValue}>
      {children}
    </PedidoContext.Provider>
  );
};
